import { useState, useCallback } from 'react';
import { NotificationData, NotificationType, NotificationAction } from '../components/ui/Notification';

export interface AddNotificationOptions {
  duration?: number;
  persistentId?: string;
  actions?: NotificationAction[];
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const addNotification = useCallback((
    type: NotificationType,
    title: string,
    message?: string,
    durationOrOptions?: number | AddNotificationOptions
  ) => {
    const options: AddNotificationOptions =
      typeof durationOrOptions === 'number'
        ? { duration: durationOrOptions }
        : durationOrOptions || {};

    if (options.persistentId) {
      setNotifications((prev) => {
        if (prev.some((n) => n.persistentId === options.persistentId)) {
          return prev;
        }
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        return [
          ...prev,
          {
            id,
            type,
            title,
            message,
            duration: options.duration ?? 5000,
            persistentId: options.persistentId,
            actions: options.actions,
          },
        ];
      });
      return options.persistentId;
    }

    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const notification: NotificationData = {
      id,
      type,
      title,
      message,
      duration: options.duration ?? 5000,
      actions: options.actions,
    };

    setNotifications((prev) => [...prev, notification]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const removeByPersistentId = useCallback((persistentId: string) => {
    setNotifications((prev) => prev.filter((n) => n.persistentId !== persistentId));
  }, []);

  const upsertByPersistentId = useCallback((
    persistentId: string,
    type: NotificationType,
    title: string,
    message?: string,
    options?: AddNotificationOptions
  ) => {
    setNotifications((prev) => {
      const idx = prev.findIndex((n) => n.persistentId === persistentId);
      if (idx === -1) {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        return [
          ...prev,
          {
            id,
            type,
            title,
            message,
            duration: options?.duration ?? 0,
            persistentId,
            actions: options?.actions,
          },
        ];
      }
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        type,
        title,
        message,
        duration: options?.duration ?? next[idx].duration,
        actions: options?.actions ?? next[idx].actions,
      };
      return next;
    });
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((title: string, message?: string, duration?: number) => {
    return addNotification('success', title, message, duration);
  }, [addNotification]);

  const showError = useCallback((title: string, message?: string, duration?: number) => {
    return addNotification('error', title, message, duration);
  }, [addNotification]);

  const showWarning = useCallback((title: string, message?: string, duration?: number) => {
    return addNotification('warning', title, message, duration);
  }, [addNotification]);

  const showInfo = useCallback((title: string, message?: string, duration?: number) => {
    return addNotification('info', title, message, duration);
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    removeNotification,
    removeByPersistentId,
    upsertByPersistentId,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
